// src/pages/markets/board/api/eventBatcher.ts

import type { QueryClient } from '@tanstack/react-query';
import { fetchBoardEventsByIds, MAX_EVENT_IDS } from './boardApi';
import type { ScreenerEvent } from './boardTypes';

// A microtask is too early: react-query starts each card's fetch in its own
// task, so a microtask flush degenerates to one request per event. A short
// window catches a whole mount burst or tick refetch in one request.
const FLUSH_WINDOW_MS = 15;

interface Pending {
    resolve: (e: ScreenerEvent) => void;
    reject: (err: unknown) => void;
}

class EventBatcher {
    private pending = new Map<string, Pending[]>();
    private timer: ReturnType<typeof setTimeout> | null = null;

    load(eventId: string): Promise<ScreenerEvent> {
        return new Promise<ScreenerEvent>((resolve, reject) => {
            const list = this.pending.get(eventId);
            if (list) list.push({ resolve, reject });
            else this.pending.set(eventId, [{ resolve, reject }]);
            if (this.timer === null) {
                this.timer = setTimeout(() => void this.flush(), FLUSH_WINDOW_MS);
            }
        });
    }

    private async flush(): Promise<void> {
        this.timer = null;
        const batch = this.pending;
        this.pending = new Map();
        if (batch.size === 0) return;

        const ids = [...batch.keys()];
        const chunks: string[][] = [];
        for (let i = 0; i < ids.length; i += MAX_EVENT_IDS) {
            chunks.push(ids.slice(i, i + MAX_EVENT_IDS));
        }

        try {
            const responses = await Promise.all(chunks.map(c => fetchBoardEventsByIds(c)));
            const byId = new Map<string, ScreenerEvent>();
            for (const r of responses) for (const e of r.events) byId.set(e.event_id, e);

            for (const [id, waiting] of batch) {
                const e = byId.get(id);
                if (e) for (const p of waiting) p.resolve(e);
                else for (const p of waiting) p.reject(new Error(`Event ${id} not found`));
            }
        } catch (err) {
            for (const waiting of batch.values()) for (const p of waiting) p.reject(err);
        }
    }
}

const batchers = new WeakMap<QueryClient, EventBatcher>();

export function getEventBatcher(qc: QueryClient): EventBatcher {
    let b = batchers.get(qc);
    if (!b) {
        b = new EventBatcher();
        batchers.set(qc, b);
    }
    return b;
}